// Small signals between a page and the shell frame around it:
// - a page with its own search box registers it, so "/" focuses that box instead of the
//   top-bar search;
// - a page showing the full "You're offline" card says so, so the frame doesn't repeat the
//   same message in the offline banner (UI0A-205).

import { useEffect, useSyncExternalStore, type RefObject } from 'react';

let pageSearch: HTMLElement | null = null;
const searchListeners = new Set<() => void>();
const notifySearch = () => searchListeners.forEach((l) => l());

/** Registers the page's search field while the page is mounted. */
export function usePageSearch(ref: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const el = ref.current;
    pageSearch = el;
    notifySearch();
    return () => {
      if (pageSearch === el) {
        pageSearch = null;
        notifySearch();
      }
    };
  }, [ref]);
}

/** True while the page has its own search field (then "/" goes there, not to the top bar). */
export function usePageHasSearch(): boolean {
  return useSyncExternalStore(
    (l) => {
      searchListeners.add(l);
      return () => searchListeners.delete(l);
    },
    () => pageSearch !== null,
    () => false,
  );
}

/** Focuses the page's own search field; false when the page has none. */
export function focusPageSearch(): boolean {
  if (!pageSearch?.isConnected) return false;
  pageSearch.focus();
  if (pageSearch instanceof HTMLInputElement) pageSearch.select();
  return true;
}

let offlineCards = 0;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

/** Marks that the page is showing the offline card (while `shown` and mounted). */
export function useOfflineCard(shown: boolean): void {
  useEffect(() => {
    if (!shown) return;
    offlineCards += 1;
    notify();
    return () => {
      offlineCards -= 1;
      notify();
    };
  }, [shown]);
}

/** True while some page shows the offline card. */
export function useOfflineCardShown(): boolean {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => offlineCards > 0,
    () => false,
  );
}
